/**
 * 実機での挙動を「推測ではなく実測」するための診断。
 *
 * 仕様書やソースから予測した内容 (docs/feasibility.md) が、目の前の端末・
 * OS バージョンで本当にその通りかを確かめるために使う。
 * すべてユーザー操作 (クリック) の中から呼ぶこと。autoplay policy により
 * ジェスチャ外では play() が拒否される。
 */

export type ProbeVerdict = 'yes' | 'no' | 'inconclusive';

export interface ProbeResult {
  verdict: ProbeVerdict;
  detail: string;
}

/** 16bit PCM の WAV を data URI として生成する (外部ファイル不要)。 */
function toneWav(seconds: number, freq: number, amplitude: number): string {
  const rate = 8000;
  const frames = Math.floor(rate * seconds);
  const dataBytes = frames * 2;
  const buf = new ArrayBuffer(44 + dataBytes);
  const view = new DataView(buf);
  const ascii = (offset: number, s: string) => {
    for (let i = 0; i < s.length; i++) view.setUint8(offset + i, s.charCodeAt(i));
  };
  ascii(0, 'RIFF');
  view.setUint32(4, 36 + dataBytes, true);
  ascii(8, 'WAVE');
  ascii(12, 'fmt ');
  view.setUint32(16, 16, true);
  view.setUint16(20, 1, true); // PCM
  view.setUint16(22, 1, true); // mono
  view.setUint32(24, rate, true);
  view.setUint32(28, rate * 2, true);
  view.setUint16(32, 2, true);
  view.setUint16(34, 16, true);
  ascii(36, 'data');
  view.setUint32(40, dataBytes, true);
  for (let i = 0; i < frames; i++) {
    const v = Math.sin((2 * Math.PI * freq * i) / rate) * amplitude * 32767;
    view.setInt16(44 + i * 2, v, true);
  }
  let bin = '';
  const bytes = new Uint8Array(buf);
  for (let i = 0; i < bytes.length; i++) bin += String.fromCharCode(bytes[i]);
  return `data:audio/wav;base64,${btoa(bin)}`;
}

const wait = (ms: number) => new Promise((r) => setTimeout(r, ms));

/**
 * HTMLMediaElement.volume が JS から本当に効くかを実測する。
 *
 * 注意: WebKit の実装は「代入を受け付けたあと非同期タスクで元に戻す」ため、
 * 同期的に読み戻すと成功したように見える。必ず待ってから読むこと。
 */
export async function probeVolumeControl(): Promise<ProbeResult> {
  const el = document.createElement('audio');
  el.src = toneWav(0.4, 440, 0.0001); // 実質無音
  el.preload = 'auto';
  el.style.display = 'none';
  document.body.appendChild(el);
  try {
    try {
      await el.play();
    } catch {
      return { verdict: 'inconclusive', detail: 'play() が拒否されました (ユーザー操作内から実行してください)' };
    }
    el.volume = 0.37;
    const immediate = el.volume;
    await wait(300);
    const settled = el.volume;
    if (Math.abs(settled - 0.37) < 0.001) {
      return { verdict: 'yes', detail: `volume=0.37 が保持されました (即値 ${immediate}, 300ms 後 ${settled})` };
    }
    return {
      verdict: 'no',
      detail: `volume が ${settled} に戻されました (即値 ${immediate})。この端末では JS からの音量制御は無効です`,
    };
  } finally {
    el.pause();
    el.remove();
  }
}

/**
 * audio 要素どうしの同時再生を実測する。
 *
 * WebKit のソース上、ConcurrentPlaybackNotPermitted は MediaType::VideoAudio
 * にのみ付与されるため、audio 要素 (MediaType::Audio) 同士は共存できるはず。
 * ここが "yes" でも「YouTube/Twitch プレイヤー (= video 要素) を 2 つ同時に
 * 鳴らせる」ことにはならない点に注意。
 */
export async function probeDualAudioElements(): Promise<ProbeResult> {
  const mk = (freq: number) => {
    const el = document.createElement('audio');
    el.src = toneWav(3, freq, 0.0002);
    el.loop = true;
    el.style.display = 'none';
    document.body.appendChild(el);
    return el;
  };
  const a = mk(330);
  const b = mk(440);
  try {
    try {
      await a.play();
      await wait(150);
      await b.play();
    } catch {
      return { verdict: 'inconclusive', detail: 'play() が拒否されました (ユーザー操作内から実行してください)' };
    }
    await wait(900);
    if (!a.paused && !b.paused) {
      return { verdict: 'yes', detail: 'audio 要素 2 本が同時に再生を維持しました' };
    }
    return {
      verdict: 'no',
      detail: `一方が停止しました (A.paused=${a.paused}, B.paused=${b.paused})`,
    };
  } finally {
    a.pause();
    b.pause();
    a.remove();
    b.remove();
  }
}

/** Web Audio の AudioContext が使えるか (ミキシング可否の前提条件)。 */
export async function probeWebAudio(): Promise<ProbeResult> {
  const Ctx =
    window.AudioContext ??
    (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
  if (!Ctx) return { verdict: 'no', detail: 'AudioContext が存在しません' };
  const ctx = new Ctx();
  try {
    await ctx.resume();
    const ok = ctx.state === 'running';
    return {
      verdict: ok ? 'yes' : 'inconclusive',
      detail: `AudioContext.state=${ctx.state} / sampleRate=${ctx.sampleRate}`,
    };
  } finally {
    void ctx.close();
  }
}
